import { Header } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { Stack } from '@mui/material';
import { Checkbox, FieldWrapper } from 'common/form';
import { CampaignCreationNextButton } from 'modules/campaign-creation/components/CampaignCreationNextButton';
import { CampaignCreationChooseAnalysis } from 'modules/campaign-creation/components/CampaignCreationChooseAnalysis';
import { useSelectedCandidateBatchStatusesHandler } from 'modules/campaign-creation/hooks/useSelectedCandidateBatchStatusesHandler';
import { AnchorText } from 'components/Text';
import { useEffect, useState } from 'react';
import { Loading } from 'common/ui/Loading';
import { CandidateStatus } from 'generated-graphql/types';
import { getTitle } from 'modules/campaign-creation/utils/getTitle';
import { useCandidateStatusCountByBatch } from 'modules/campaign-creation/hooks/useCandidateStatusCountByBatch';
import { CampaignCreationSkipButton } from 'modules/campaign-creation/components/CampaignCreationSkipButton';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { candidateStatusLabelMap } from 'services/candidate/utils/candidateStatusLabelMap';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

export const CampaignCreationAnalysisSetupStep: React.FC = () => {
  const [selectedStatuses, setSelectedStatuses] = useState<CandidateStatus[]>(
    []
  );
  const [changeAnalysis, setChangeAnalysis] = useState(false);
  const onChooseAnalysisSubmit = useCampaignCreationStore(
    (store) => store.onChooseAnalysisSubmit
  );
  const onAnalysisSetupBack = useCampaignCreationStore(
    (store) => store.onAnalysisSetupBack
  );
  const onAnalysisSetupSkip = useCampaignCreationStore(
    (store) => store.onAnalysisSetupSkip
  );
  const onChooseAnalysisSkip = useCampaignCreationStore(
    (store) => store.onChooseAnalysisSkip
  );
  const state = useCampaignCreationStore((store) => store.state);
  const [hasError, setHasError] = useState(false);
  const { openModal } = useModalV2();
  // Flag defaults on; only an explicit `false` from PostHog hides the button
  const isSkipEnabled = useFlag(Flags.CAMPAIGN_ANALYSIS_SKIP_BUTTON) !== false;

  const {
    loading,
    selectedCandidateBatchStatusesId,
    candidateBatchId,
    candidateBatchName,
    candidateBatchStatuses: selectedCandidateBatchStatuses,
    onUpdateSelectedCandidateBatchId,
    onUpdateSelectedCandidateBatchStatuses
  } = useSelectedCandidateBatchStatusesHandler();

  const { statusOptions, loading: statusOptionsLoading } =
    useCandidateStatusCountByBatch(candidateBatchId);

  useEffect(() => {
    if (selectedCandidateBatchStatuses) {
      setSelectedStatuses(selectedCandidateBatchStatuses as CandidateStatus[]);
    }
  }, [selectedCandidateBatchStatuses]);

  const handleSelectAnalysis = async (analysisId: string) => {
    await onUpdateSelectedCandidateBatchId(analysisId);
    setChangeAnalysis(false);
  };

  const handleChooseAnalysisSkip = async () => {
    if (selectedCandidateBatchStatusesId) {
      await onUpdateSelectedCandidateBatchStatuses([]);
    }
    onChooseAnalysisSkip();
  };

  const handleNext = async () => {
    if (selectedStatuses.length === 0) {
      setHasError(true);
      return;
    }

    const jobTitle =
      state?.jobDescriptionParsedState?.jobDescriptionParsed?.jobTitle || '';

    // Create status list for the dialog
    const statusLabels = selectedStatuses
      .map((status) => `• ${candidateStatusLabelMap[status]}`)
      .join('\n');

    openModal({
      title: 'Confirm recipients',
      subtitle: `Send this campaign to ${jobTitle} applicants who are:\n\n${statusLabels}`,
      onPrimaryAction: async () => {
        await onUpdateSelectedCandidateBatchStatuses(selectedStatuses);
        onChooseAnalysisSubmit({
          analysisId: selectedCandidateBatchStatusesId
        });
      },
      primaryButtonText: 'Confirm',
      secondaryButtonText: 'Cancel'
    });
  };

  const onBack = () => {
    if (selectedCandidateBatchStatusesId) {
      setChangeAnalysis(false);
      return;
    }
    onAnalysisSetupBack();
  };

  const handleAnalysisSetupSkip = async () => {
    if (selectedCandidateBatchStatusesId) {
      await onUpdateSelectedCandidateBatchStatuses([]);
    }
    onAnalysisSetupSkip();
  };

  if (loading || statusOptionsLoading) {
    return (
      <Stack width="100%" justifyContent="center" alignItems="center">
        <Loading />
      </Stack>
    );
  }

  if (!selectedCandidateBatchStatusesId || changeAnalysis) {
    return (
      <CampaignCreationChooseAnalysis
        onSelectAnalysis={handleSelectAnalysis}
        onBack={onBack}
        onSkip={handleAnalysisSetupSkip}
      />
    );
  }

  return (
    <>
      <Header
        currentBreadcrumb={getTitle(state)}
        title="Choose applicants"
        breadcrumbs={[
          {
            name: 'Campaigns',
            path: Paths.CAMPAIGNS
          }
        ]}
        onBack={onAnalysisSetupBack}
      />
      <Stack spacing="40px">
        <FieldWrapper
          label={
            <>
              Choose from this analysis:
              <AnchorText
                sx={{
                  pl: 1,
                  fontWeight: 'bold'
                }}
                onClick={() =>
                  window.open(`${Paths.ANALYSE}/${candidateBatchId}`, '_blank')
                }
              >
                {candidateBatchName}
              </AnchorText>
            </>
          }
          errorMessage={hasError ? 'Choose at least one status.' : undefined}
          subLabel={
            <AnchorText onClick={() => setChangeAnalysis(true)}>
              Change analysis
            </AnchorText>
          }
        >
          <Stack spacing="15px">
            {statusOptions.map((option) => (
              <Checkbox
                key={option.value}
                field={{ checkboxLabel: option.label }}
                value={selectedStatuses.includes(option.value)}
                onChange={() => {
                  setSelectedStatuses((prev) => {
                    if (prev.includes(option.value)) {
                      if (prev.length === 1) {
                        setHasError(true);
                      } else {
                        setHasError(false);
                      }
                      return prev.filter((status) => status !== option.value);
                    }
                    setHasError(false);
                    return [...prev, option.value];
                  });
                }}
              />
            ))}
          </Stack>
        </FieldWrapper>
        <Stack direction="row" spacing="15px">
          <CampaignCreationNextButton handleNext={handleNext}>
            {isSkipEnabled && (
              <CampaignCreationSkipButton
                onClick={handleChooseAnalysisSkip}
                text="Do this later"
              />
            )}
          </CampaignCreationNextButton>
        </Stack>
      </Stack>
    </>
  );
};
